import logger from '@leaflink/snitch';
import { unrefElement } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import type { UseSortableOptions, UseSortableReturn } from './useSortable.types';
import { createGhostElement, getValidDragTarget, moveSortableElements } from './useSortable.utils';

export * from './useSortable.types';
export { moveSortableElements } from './useSortable.utils';

export default function useSortable<SortableItem>({
  isEnabled = true,
  sortInPlace = true,
  ...options
}: UseSortableOptions<SortableItem>): UseSortableReturn {
  const parentEl = computed(() => unrefElement(options.ref));

  const newIndex = ref(-1);
  const oldIndex = ref(-1);

  const isDragging = computed(() => newIndex.value !== -1);

  const ghostClasses = computed(() => options.ghostClass?.split(' ') || []);
  const chosenClasses = computed(() => options.chosenClass?.split(' ') || []);

  const handleDragStart = (e: DragEvent) => {
    if (!e.dataTransfer || !parentEl.value) return;
    e.dataTransfer.setData('text/plain', ''); // Required for Firefox
    e.dataTransfer.effectAllowed = 'move';

    try {
      const target = getValidDragTarget(e.target as HTMLElement);

      const index = Array.from(parentEl.value.children).indexOf(target);
      if (index === -1) return;

      const { cleanupGhostElement } = createGhostElement(e, ghostClasses.value);

      oldIndex.value = newIndex.value = index;

      target.classList.add(...chosenClasses.value);

      cleanupGhostElement();

      options.onDragStart?.({ ...e, oldIndex: index });
    } catch (error) {
      logger.error(`Error in drag start: ${error}`);
    }
  };

  const handleDragEnter = (e: DragEvent) => {
    if (!e.dataTransfer || !parentEl.value) return;
    e.dataTransfer.dropEffect = 'move';

    try {
      const target = getValidDragTarget(e.target as HTMLElement);

      const index = Array.from(parentEl.value.children).indexOf(target);
      if (index === -1 || index === newIndex.value) return;

      const draggedEl = parentEl.value.children[newIndex.value];
      parentEl.value.insertBefore(draggedEl, index < newIndex.value ? target : target.nextSibling);

      newIndex.value = index;
    } catch (error) {
      logger.error(`Error in drag enter: ${error}`);
    }
  };

  function handleDragOver(e: DragEvent) {
    e.preventDefault(); // Required to allow dropping
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();

    if (!parentEl.value) return;

    try {
      if (oldIndex.value === newIndex.value || !sortInPlace) return;
      moveSortableElements({ list: options.list, from: oldIndex.value, to: newIndex.value });
    } catch (error) {
      logger.error(`Error in drag drop: ${error}`);
    }
  }

  function handleDragEnd(e: DragEvent) {
    try {
      const target = getValidDragTarget(e.target as HTMLElement);

      target.classList.remove(...chosenClasses.value);

      options.onDragEnd?.({
        ...e,
        oldIndex: oldIndex.value,
        newIndex: newIndex.value,
      });

      oldIndex.value = newIndex.value = -1;
    } catch (error) {
      logger.error(`Error in drag end: ${error}`);
    }
  }

  watch(
    () => isEnabled,
    () => (isEnabled ? setupListeners() : removeListeners()),
  );

  function setupListeners() {
    if (!parentEl.value) return;

    parentEl.value.addEventListener('dragstart', handleDragStart);
    parentEl.value.addEventListener('dragend', handleDragEnd);
    parentEl.value.addEventListener('dragenter', handleDragEnter);
    parentEl.value.addEventListener('dragover', handleDragOver);
    parentEl.value.addEventListener('drop', handleDrop);
  }

  function removeListeners() {
    if (!parentEl.value) return;

    parentEl.value.removeEventListener('dragstart', handleDragStart);
    parentEl.value.removeEventListener('dragend', handleDragEnd);
    parentEl.value.removeEventListener('dragenter', handleDragEnter);
    parentEl.value.removeEventListener('dragover', handleDragOver);
    parentEl.value.removeEventListener('drop', handleDrop);
  }

  onMounted(() => {
    if (!isEnabled) return;
    setupListeners();
  });

  onBeforeUnmount(removeListeners);

  return {
    isDragging,
    oldIndex,
    newIndex,
  };
}
